import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, LogIn, Search, ShieldCheck, Plus, Pencil, MapPin, CreditCard, Trash2, Calendar, Printer } from "lucide-react";

import AxiosDB from "@/Config/AxiosDB";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PROVINCIAS, getMunicipios } from "@/data/provinciasMunicipios";
import moment from "moment";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CertificadoCard, { CertificadoInfo, getEstadoCertificado } from "./certificadoCard";
import { formatoCertificado } from "./FormatoCertificacion";

const EMPTY_FORM = {
    rnc: "", usuario: "", nombre: "", correo: "", telefono: "", password: "",
    provincia_codigo: "", provincia: "", municipio_codigo: "", municipio: "",
    direccion: ''
};

export default function AdminEmpresas({ user, onLogout, onImpersonate }) {
    const [empresas, setEmpresas] = useState([]);
    const [filteredEmpresas, setFilteredEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null); // null => crear
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    // Validación de empresa_id con Alanube
    const [idDialogOpen, setIdDialogOpen] = useState(false);
    const [empresaIdInput, setEmpresaIdInput] = useState("");
    const [validating, setValidating] = useState(false);

    // Gestión de suscripciones
    const [subDialogOpen, setSubDialogOpen] = useState(false);
    const [subEmpresa, setSubEmpresa] = useState(null);
    const [subData, setSubData] = useState(null);
    const [subLoading, setSubLoading] = useState(false);
    const [subSaving, setSubSaving] = useState(false);
    const [nuevaSubDialogOpen, setNuevaSubDialogOpen] = useState(false);
    const [subToDelete, setSubToDelete] = useState(null);
    const EMPTY_SUB = { plan_nombre: "Plan Profesional", cantidad_documentos: "", precio: "", fecha_desde: "", fecha_hasta: "", notas: "" };
    const [subForm, setSubForm] = useState(EMPTY_SUB);

    useEffect(() => {
        if (user?.role === "superadmin") {
            loadEmpresas();
        } else {
            toast.error("Acceso denegado. Se requiere rol de SuperAdmin");
        }
    }, [user]);

    useEffect(() => {
        applyFilters();
    }, [empresas, searchTerm]);

    const loadEmpresas = async () => {
        try {
            const response = await AxiosDB.get(`/admin/empresas`);
            setEmpresas(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al cargar empresas");
        } finally {
            setLoading(false);
        }
    };

    const abrirCrear = () => {
        setEmpresaIdInput("");
        setIdDialogOpen(true);
    };


    const handleValidarEmpresaId = async () => {
        const eid = empresaIdInput.trim();
        if (!eid) {
            toast.error("Ingresa el ID de empresa (empresa_id)");
            return;
        }
        setValidating(true);
        try {
            const res = await AxiosDB.get(`${API}/admin/empresa/alanube/${encodeURIComponent(eid)}`);
            const cfg = res.data;
            setEditId(null);

            console.log(cfg)
            setForm({
                ...EMPTY_FORM,
                usuario: cfg.identification,
                empresa_id: cfg.empresa_id || eid,
                rnc: cfg.identification || "",
                nombre: cfg.name || "",
                nombre_comercial: cfg.tradeName || "",
                correo: cfg.correo || "",
                telefono: cfg.telefono || "",
                provincia_codigo: cfg.provincia_codigo || "",
                provincia: cfg.province || "",
                municipio_codigo: cfg.municipio_codigo || "",
                municipio: cfg.municipality || "",
                certificado: cfg.certificate
            });
            setIdDialogOpen(false);
            setDialogOpen(true);
            toast.success("Empresa validada en Alanube. Datos cargados.");
        } catch (error) {
            toast.error(error.response?.data?.detail || "No se pudo validar el ID con Alanube");
        } finally {
            setValidating(false);
        }
    };

    const abrirEditar = (emp) => {
        setEditId(emp.empresa_id);
        console.log(emp)
        setForm({
            idcompany: emp.idcompany,
            rnc: emp.rnc || "",
            nombre: emp.nombre_social,
            nombre_comercial: emp.nombre_comercial,
            usuario: emp.usuario || "",
            correo: emp.correo_electronico || "",
            telefono: emp.telefono || "",
            password: "",
            provincia_codigo: emp.provincia_code || "",
            provincia: emp.provincia || "",
            municipio_codigo: emp.municipio_code || "",
            municipio: emp.municipio || "",
            website: emp.sitio_web,
            certificado: emp.certificado,
            direccion: emp.direccion,

            plan_name: emp.plan_name,
            plan_cantidad: emp.plan_cantidad,
            plan_precio: emp.plan_precio

        });
        setDialogOpen(true);
    };

    const onProvinciaChange = (codigo) => {
        const prov = PROVINCIAS.find((p) => p.codigo === codigo);
        setForm((f) => ({
            ...f,
            provincia_codigo: codigo,
            provincia: prov?.nombre || "",
            municipio_codigo: "",
            municipio: "",
        }));
    };

    const onMunicipioChange = (codigo) => {
        const mun = getMunicipios(form.provincia_codigo).find((m) => m.codigo === codigo);
        setForm((f) => ({ ...f, municipio_codigo: codigo, municipio: mun?.nombre || "" }));
    };

    const handleSubmit = async () => {
        if (!editId && (!form.rnc || !form.usuario || !form.password)) {
            toast.error("RNC, usuario y contraseña son obligatorios");
            return;
        }
        if (!form.nombre || !form.correo) {
            toast.error("Nombre y correo son obligatorios");
            return;
        }
        setSaving(true);
        try {
            if (editId) {
                const payload = {
                    nombre: form.nombre,
                    nombre_comercial: form.nombre_comercial,
                    correo: form.correo,
                    telefono: form.telefono,
                    provincia_codigo: form.provincia_codigo,
                    provincia: form.provincia,
                    municipio_codigo: form.municipio_codigo,
                    municipio: form.municipio,
                    direccion: form.direccion,
                    usuario: form.usuario,
                    website: form.sitio_web,
                    plan_name: form.plan_name,
                    plan_cantidad: form.plan_cantidad,
                    plan_precio: form.plan_precio
                };
                if (form.password) payload.password = form.password;

                console.log(form)
                await AxiosDB.put(`${API}/admin/empresas/${editId}`, payload);
                toast.success("Empresa actualizada");
            } else {
                await AxiosDB.post(`${API}/admin/empresas`, form);
                toast.success("Empresa creada exitosamente");
            }
            setDialogOpen(false);
            loadEmpresas();
        } catch (error) {
            console.log(error)
            toast.error(error.response?.data?.detail || "Error al guardar la empresa");
        } finally {
            setSaving(false);
        }
    };



    const applyFilters = () => {
        if (!searchTerm) {
            setFilteredEmpresas(empresas);
            return;
        }

        const search = searchTerm.toLowerCase();
        const filtered = empresas.filter(
            (emp) =>
                emp.nombre.toLowerCase().includes(search) ||
                emp.rnc.toLowerCase().includes(search) ||
                emp.usuario.toLowerCase().includes(search) ||
                emp.correo.toLowerCase().includes(search)
        );
        setFilteredEmpresas(filtered);
    };


    const abrirSuscripciones = async (empresa) => {
        setSubEmpresa(empresa);
        setSubForm(EMPTY_SUB);
        setSubData(null);
        setSubDialogOpen(true);
        setSubLoading(true);
        try {
            const res = await AxiosDB.get(`${API}/admin/empresas/${empresa.id}/suscripciones`);
            setSubData(res.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al cargar suscripciones");
        } finally {
            setSubLoading(false);
        }
    };

    const recargarSuscripciones = async (empresaId) => {
        try {
            const res = await AxiosDB.get(`${API}/admin/empresas/${empresaId}/suscripciones`);
            setSubData(res.data);
        } catch (error) {
            toast.error("Error al recargar suscripciones");
        }
    };

    const abrirNuevaSuscripcion = () => {
        setSubForm(EMPTY_SUB);
        setNuevaSubDialogOpen(true);
    };

    const handleCrearSuscripcion = async () => {
        if (!subForm.cantidad_documentos || Number(subForm.cantidad_documentos) <= 0) {
            toast.error("Ingresa una cantidad de documentos válida");
            return;
        }
        if (!subForm.fecha_desde || !subForm.fecha_hasta) {
            toast.error("Las fechas desde y hasta son obligatorias");
            return;
        }
        if (subForm.fecha_hasta < subForm.fecha_desde) {
            toast.error("La fecha 'hasta' no puede ser anterior a la fecha 'desde'");
            return;
        }
        setSubSaving(true);
        try {
            await AxiosDB.post(`${API}/admin/empresas/${subEmpresa.id}/suscripciones`, {
                plan_nombre: subForm.plan_nombre || "Plan Profesional",
                cantidad_documentos: Number(subForm.cantidad_documentos),
                precio: Number(subForm.precio) || 0,
                fecha_desde: subForm.fecha_desde,
                fecha_hasta: subForm.fecha_hasta,
                notas: subForm.notas || "",
            });
            toast.success("Suscripción agregada");
            setSubForm(EMPTY_SUB);
            recargarSuscripciones(subEmpresa.id);
            setNuevaSubDialogOpen(false)
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al crear la suscripción");
        } finally {
            setSubSaving(false);
        }
    };

    const handleEliminarSuscripcion = async () => {
        if (!subToDelete) return;
        try {
            await AxiosDB.delete(`${API}/admin/empresas/${subEmpresa.id}/suscripciones/${subToDelete.id}`);
            toast.success("Suscripción eliminada");
            recargarSuscripciones(subEmpresa.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al eliminar la suscripción");
        } finally {
            setSubToDelete(null);
        }
    };


    const handleImprimirPDF = () => {
        const fecha = new Date().toLocaleString("es-DO");
        const fmt = (n) => "RD$" + Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const rows = filteredEmpresas.filter(r => r.id !== 1).map((e) => {

            const estadoCertificado = e?.certificado?.endDate
                ? getEstadoCertificado(e.certificado.endDate)
                : {
                    texto: "Sin certificado",
                    color: "#6c757d"
                };


            return `
                <tr>
                    <td>${e.rnc || ""}</td>
                    <td>${e.nombre || ""}</td>
                    <td>${e.telefono || ""}</td>
                    <td style="text-align:center" class={${estadoCertificado.color}>
                       
                            ${estadoCertificado.texto}
                        
                    </td>
                    <td>${e?.plan_name || ""}</td>
                    <td style="text-align:right">
                        ${(e?.plan_cantidad || 0).toLocaleString("es-DO")}
                    </td>
                    <td style="text-align:right">
                        ${fmt(e?.plan_precio || 0)}
                    </td>

                    

                </tr>`;
        }).join("");

        const totalDocs = filteredEmpresas.reduce((a, e) => a + (e.cantidad_documentos || 0), 0);
        const totalMonto = filteredEmpresas.reduce((a, e) => a + parseFloat((e?.plan_precio || 0)), 0);
        const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8" />
      <title>Listado de Empresas</title>
      <style>
        * { font-family: Arial, Helvetica, sans-serif; }
        body { margin: 32px; color: #1e293b; }
        h1 { font-size: 20px; margin: 0 0 4px; }
        .meta { font-size: 12px; color: #64748b; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; }
        thead th { background: #0f172a; color: #fff; }
        tbody tr:nth-child(even) { background: #f1f5f9; }
        tfoot td { font-weight: bold; background: #e2e8f0; }
        @media print { body { margin: 12px; } }
      </style></head>
      <body>
        <h1>Listado de Empresas</h1>
        <div class="meta">Buz&oacute;n Fiscal &middot; Generado: ${fecha} &middot; Total empresas: ${filteredEmpresas.length}</div>
        <table>
          <thead>
            <tr>
              <th>RNC</th><th>Nombre</th><th>Tel&eacute;fono</th><th>Estado Certificado</th><th>Plan Activo</th>
              <th style="text-align:right">Cant. Documentos</th><th style="text-align:right">Monto</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="6">TOTALES</td>
              <td style="text-align:right">${fmt(totalMonto)}</td>
            </tr>
          </tfoot>
        </table>
        <script>window.onload = function(){ window.print(); }<\/script>
      </body></html>`;
        // const w = window.open("", "_blank");
        // if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
        // w.document.write(html);
        // w.document.close();

        const pri =
            document.getElementById(
                "ifmcontentstoprint"
            ).contentWindow;
        pri.document.open();
        pri.document.write(html);
        pri.document.close();

        // setTimeout(async () => {
        //     pri.focus();
        //     await pri.print();
        // }, 1500);
    };


    const ImprimirCertificadoPDF = () => {
        const fecha = new Date().toLocaleString("es-DO");
        const fmt = (n) => "RD$" + Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

        console.log(form)
        //form 
        const totalDocs = filteredEmpresas.reduce((a, e) => a + (e.cantidad_documentos || 0), 0);
        const totalMonto = filteredEmpresas.reduce((a, e) => a + parseFloat((e?.plan_precio || 0)), 0);
        const html = formatoCertificado
            .replaceAll('{rnc}', form.rnc)
            .replaceAll('{nombre}', form.nombre)
            .replaceAll('{direccion}', form.direccion)
            .replaceAll('{servicio}', 'Arrendamiento de Intangibles – API de Facturación Electrónica')
            .replaceAll('{cantidad}', form.plan_cantidad)
            .replaceAll('{monto}', Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }).format(form.plan_precio))
            .replaceAll('{valid_certificado}', form?.certificado?.endDate || '')
            .replaceAll('{certificacion}', `DASOFT-2026-${String(editId).padStart(3, '0')}`)
            .replaceAll('{fecha}', fecha)
            .replaceAll('{email}', form.correo)
        // const w = window.open("", "_blank");
        // if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
        // w.document.write(html);
        // w.document.close();



        const pri =
            document.getElementById(
                "ifmcontentstoprint"
            ).contentWindow;
        pri.document.open();
        pri.document.write(html);
        pri.document.close();


        // setTimeout(async () => {
        //     pri.focus();
        //     await pri.print();
        // }, 1500);
    };

    const handleImpersonate = async (empresa) => {
        try {
            const response = await AxiosDB.post(`/admin/impersonate/${empresa.id}`);

            // Actualizar token y usuario
            localStorage.setItem("token", response.data.access_token);
            localStorage.setItem("impersonating", "true");
            localStorage.setItem("superadmin_name", user.nombre);

            // Llamar callback para actualizar el estado global
            if (onImpersonate) {
                onImpersonate(response.data.access_token, response.data.user);
            }

            toast.success(`Accediendo como: ${empresa.nombre}`);

            // Redirigir al dashboard
            window.location.href = "/";
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al acceder a la empresa");
        }
    };

    if (user?.role !== "superadmin") {
        return (
            <Layout user={user} onLogout={onLogout} title="Acceso Denegado">
                <Card>
                    <CardContent className="py-12 text-center">
                        <ShieldCheck className="w-16 h-16 mx-auto mb-4 text-red-500" />
                        <h2 className="text-xl font-bold text-slate-800 mb-2">Acceso No Autorizado</h2>
                        <p className="text-slate-600">Esta página solo está disponible para SuperAdministradores</p>
                    </CardContent>
                </Card>
            </Layout>
        );
    }


    const municipiosForm = getMunicipios(form.provincia_codigo);
    return (
        <Layout user={user} onLogout={onLogout} title="Administración de Empresas">
            {/* Indicador de SuperAdmin */}
            <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-600 rounded-lg">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-purple-900">Panel de Super Administrador</h3>
                            <p className="text-sm text-purple-700">Gestiona y accede a todas las empresas del sistema</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card data-testid="admin-empresas-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Empresas Registradas</CardTitle>
                        <CardDescription>Lista de todas las empresas en el sistema - Total: {empresas.length}</CardDescription>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleImprimirPDF} data-testid="imprimir-listado-button">
                            <Printer className="w-4 h-4 mr-2" /> Imprimir PDF
                        </Button>
                        <Button onClick={abrirCrear} data-testid="nueva-empresa-button">
                            <Plus className="w-4 h-4 mr-2" /> Nueva Empresa
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Búsqueda */}
                    <div className="mb-6">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                            <Input
                                placeholder="Buscar por nombre, RNC, usuario o correo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-empresas-input"
                            />
                        </div>
                        {searchTerm && (
                            <p className="text-sm text-slate-600 mt-2">
                                Mostrando {filteredEmpresas.length} de {empresas.length} empresas
                            </p>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center py-8 text-slate-600">Cargando empresas...</div>
                    ) : filteredEmpresas.length === 0 ? (
                        <div className="text-center py-8 text-slate-600">
                            {empresas.length === 0 ? "No hay empresas registradas" : "No se encontraron resultados"}
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="empresas-list">
                            {filteredEmpresas.map((empresa) => (
                                <Card key={empresa.id} className="card-hover" data-testid={`empresa-card-${empresa.id}`}>
                                    <CardContent className="py-4">
                                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                            {/* Info principal */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
                                                    <span className="font-bold text-slate-800 text-lg truncate">{empresa.nombre}</span>
                                                    {empresa.role === "superadmin" && (
                                                        <Badge className="bg-purple-100 text-purple-800 border-purple-200">SuperAdmin</Badge>
                                                    )}
                                                    {empresa.id === user.id && (
                                                        <Badge variant="secondary">Sesión actual</Badge>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                                                    <span><span className="text-slate-400">RNC:</span> <span className="font-semibold text-slate-700">{empresa.rnc}</span></span>
                                                    <span><span className="text-slate-400">Usuario:</span> <span className="font-semibold text-slate-700">{empresa.usuario}</span></span>
                                                    <span className="truncate"><span className="text-slate-400">Correo:</span> {empresa.correo}</span>
                                                    <span><span className="text-slate-400">Tel:</span> {empresa.telefono}</span>
                                                    {(empresa.provincia || empresa.municipio) && (
                                                        <span className="inline-flex items-center gap-1 text-rose-600">
                                                            <MapPin className="w-3.5 h-3.5" />
                                                            {[empresa.municipio, empresa.provincia].filter(Boolean).join(", ")}
                                                        </span>
                                                    )}


                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                                                    <CertificadoInfo certificado={empresa?.certificado || {}} />
                                                </div>
                                            </div>

                                            {/* Estadísticas */}
                                            <div className="flex items-center gap-6 lg:px-6 lg:border-l lg:border-r border-slate-200">
                                                <div className="text-center">
                                                    <div className="font-bold text-blue-600">{empresa.stats?.comprobantes_emitidos || 0}</div>
                                                    <div className="text-xs text-slate-500">Emitidos</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-bold text-green-600">{empresa.stats?.comprobantes_recibidos || 0}</div>
                                                    <div className="text-xs text-slate-500">Recibidos</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="font-bold text-slate-800">{empresa.stats?.total_comprobantes || 0}</div>
                                                    <div className="text-xs text-slate-500">Total</div>
                                                </div>
                                            </div>

                                            {/* Acciones */}
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Button variant="outline" size="sm" onClick={() => abrirEditar(empresa.empresa)} data-testid={`editar-empresa-${empresa.id}`}>
                                                    <Pencil className="w-4 h-4 mr-1" /> Editar
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => abrirSuscripciones(empresa)} data-testid={`suscripciones-empresa-${empresa.id}`}>
                                                    <CreditCard className="w-4 h-4 mr-1" /> Suscripciones
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    disabled={empresa.id === user.empresa_id}
                                                    onClick={() => handleImpersonate(empresa)}
                                                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                                                    data-testid={`impersonate-button-${empresa.id}`}
                                                >
                                                    <LogIn className="w-4 h-4 mr-1" /> Acceder
                                                </Button>

                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>


            {/* Diálogo: Validar empresa_id con Alanube */}
            <Dialog open={idDialogOpen} onOpenChange={setIdDialogOpen}>
                <DialogContent data-testid="empresa-id-dialog">
                    <DialogHeader>
                        <DialogTitle>Nueva Empresa — Validar con Alanube</DialogTitle>
                        <DialogDescription>
                            Ingresa el <strong>ID de empresa (empresa_id)</strong> de Alanube. Se validará para obtener la configuración inicial.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>ID Empresa (empresa_id)</Label>
                        <Input
                            value={empresaIdInput}
                            onChange={(e) => setEmpresaIdInput(e.target.value)}
                            placeholder="Ej. 01JT8JCJENZGGQQXSYC02SDA03"
                            className="font-mono"
                            data-testid="empresa-id-input"
                            onKeyDown={(e) => { if (e.key === "Enter") handleValidarEmpresaId(); }}
                        />
                        <p className="text-xs text-slate-500 mt-2">Identificador tipo ULID de 26 caracteres.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIdDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleValidarEmpresaId} disabled={validating} data-testid="validar-empresa-id-button">
                            {validating ? "Validando..." : "Validar y continuar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Diálogo Crear / Editar Empresa */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="empresa-dialog">
                    <DialogHeader>
                        <DialogTitle>{editId ? "Editar Empresa" : "Nueva Empresa"}</DialogTitle>
                        <DialogDescription>
                            {editId ? "Modifica los datos de la empresa." : "Completa los datos para registrar una nueva empresa."}
                        </DialogDescription>
                    </DialogHeader>




                    <Tabs defaultValue="info"  >
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="info" data-testid="payload-tab">Datos de la Empresa</TabsTrigger>
                            <TabsTrigger value="certificado" data-testid="factura-tab">Certificado</TabsTrigger>

                        </TabsList>

                        <TabsContent value="info" className="mt-4 h-[65vh]">
                            <form autoComplete="off">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                                    <div>
                                        <Label>RNC *</Label>
                                        <Input value={form.rnc} disabled={true} onChange={(e) => setForm({ ...form, rnc: e.target.value })} data-testid="empresa-rnc" />
                                    </div>
                                    <div>
                                        <Label>Certificado Valido Hasta</Label>
                                        <Input value={form.certificado?.endDate || ''} disabled={true} onChange={(e) => setForm({ ...form, certificado: e.target.value })} data-testid="empresa-certificado" />
                                    </div>

                                    <div className="md:col-span-2">
                                        <Label>Nombre / Razón Social *</Label>
                                        <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="empresa-nombre" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label>Nombre / Comercial *</Label>
                                        <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} data-testid="empresa-nombre_comercial" />
                                    </div>

                                    <div>
                                        <Label>Correo *</Label>
                                        <Input type="email" value={form.correo} onChange={(e) => setForm({ ...form, correo: e.target.value })} data-testid="empresa-correo" />
                                    </div>
                                    <div>
                                        <Label>Teléfono</Label>
                                        <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="empresa-telefono" />
                                    </div>
                                    <div>
                                        <Label>Provincia</Label>
                                        <Select value={form.provincia_codigo} onValueChange={onProvinciaChange}>
                                            <SelectTrigger data-testid="empresa-provincia"><SelectValue placeholder="Selecciona provincia" /></SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {PROVINCIAS.map((p) => (
                                                    <SelectItem key={p.codigo} value={p.codigo}>{p.nombre}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Municipio  </Label>
                                        <Select value={form.municipio_codigo} onValueChange={onMunicipioChange} disabled={!form.provincia_codigo}>
                                            <SelectTrigger data-testid="empresa-municipio">
                                                <SelectValue placeholder={form.provincia_codigo ? "Selecciona municipio" : "Elige provincia primero"} />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {municipiosForm.map((m) => (
                                                    <SelectItem key={m.codigo} value={m.codigo}>{m.nombre}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label>Direccion *</Label>
                                        <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} data-testid="empresa-direccion" />
                                    </div>

                                    <div>
                                        <Label>Plan Nombre *</Label>
                                        <Input autoComplete="off" type="text" value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} data-testid="empresa-plan-nombre" />
                                    </div>
                                    <div>
                                        <Label>Cantidad de Documentos Mensuales</Label>
                                        <Input autoComplete="off" value={form.plan_cantidad} onChange={(e) => setForm({ ...form, plan_cantidad: e.target.value })} data-testid="empresa-plan-cantidad" />
                                    </div>
                                    <div>
                                        <Label>Precio Mensual Contratado</Label>
                                        <Input type="number" autoComplete="off" value={form.plan_precio} onChange={(e) => setForm({ ...form, plan_precio: e.target.value })} />
                                    </div>


                                    <div style={{ display: editId ? 'none' : 'block' }}>
                                        <Label>Usuario Administrador *</Label>
                                        <Input value={form.usuario} onChange={(e) => setForm({ ...form, usuario: e.target.value })} data-testid="empresa-usuario" />
                                    </div>
                                    <div style={{ display: editId ? 'none' : 'block' }}>
                                        <Label>{editId ? "Nueva contraseña (opcional)" : "Contraseña *"}</Label>
                                        <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="empresa-password"
                                            placeholder={editId ? "Dejar en blanco para no cambiar" : ""} />
                                    </div>
                                </div>
                            </form>
                        </TabsContent>
                        <TabsContent value="certificado" className="mt-4 h-[65vh]">
                            <CertificadoCard
                                certificado={form.certificado}
                            />
                        </TabsContent>

                    </Tabs>



                    <DialogFooter>
                        <Button variant="outline" onClick={ImprimirCertificadoPDF} data-testid="imprimir-listado-button">
                            <Printer className="w-4 h-4 mr-2" /> Imprimir Certificado
                        </Button>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSubmit} disabled={saving} data-testid="guardar-empresa-button">
                            {saving ? "Guardando..." : editId ? "Guardar Cambios" : "Crear Empresa"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Diálogo: Gestión de Suscripciones */}
            <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="suscripciones-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                            Suscripciones — {subEmpresa?.nombre}
                        </DialogTitle>
                        <DialogDescription>
                            Administra las suscripciones de la empresa. Cada suscripción define una cantidad de documentos y un período de apertura (desde / hasta).
                        </DialogDescription>
                    </DialogHeader>

                    {/* Resumen */}
                    {subData && (
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                                <div className="text-2xl font-bold text-blue-600" data-testid="sub-total-contratado">{subData.documentos_contratados_activos}</div>
                                <div className="text-xs text-slate-500">Documentos contratados (activos)</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                                <div className="text-2xl font-bold text-slate-800" data-testid="sub-usados">{subData.documentos_usados}</div>
                                <div className="text-xs text-slate-500">Documentos usados</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                                <div className="text-2xl font-bold text-green-600">{Math.max(0, (subData.documentos_contratados_activos || 0) - (subData.documentos_usados || 0))}</div>
                                <div className="text-xs text-slate-500">Disponibles</div>
                            </div>
                        </div>
                    )}

                    {/* Lista de suscripciones */}
                    <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Suscripciones registradas</h4>
                        {subLoading ? (
                            <div className="text-center py-6 text-slate-500 text-sm">Cargando...</div>
                        ) : !subData || subData.suscripciones.length === 0 ? (
                            <div className="text-center py-6 text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg" data-testid="sub-empty">
                                No hay suscripciones registradas para esta empresa.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="sub-list">
                                {subData.suscripciones.map((s) => (
                                    <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`sub-item-${s.id}`}>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-slate-800 truncate">{s.plan_nombre}</span>
                                                <Badge className={
                                                    s.estado_calculado === "activa" ? "bg-green-100 text-green-800 border-green-200"
                                                        : s.estado_calculado === "vencida" ? "bg-amber-100 text-amber-800 border-amber-200"
                                                            : "bg-slate-100 text-slate-600 border-slate-200"
                                                }>{s.estado_calculado}</Badge>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 mt-1">
                                                <span><span className="text-slate-400">Documentos:</span> <span className="font-semibold">{s.documentos_contratados}</span></span>
                                                {s.precio_mensual > 0 && <span><span className="text-slate-400">Precio:</span> RD${Number(s.precio_mensual).toLocaleString('es-DO')}</span>}
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                                    {moment(s.fecha_inicio).format('YYYY-MM-DD')} → {moment(s.fecha_renovacion).format('YYYY-MM-DD')}
                                                </span>
                                            </div>
                                            {s.notas && <p className="text-xs text-slate-500 mt-1">{s.notas}</p>}
                                        </div>
                                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setSubToDelete(s)} data-testid={`eliminar-sub-${s.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}

                            </div>
                        )}
                    </div>

                    {/* Botón para agregar nueva suscripción (abre popup) */}
                    <div className="border-t pt-4 flex justify-end">
                        <Button onClick={abrirNuevaSuscripcion} data-testid="abrir-nueva-suscripcion-button">
                            <Plus className="w-4 h-4 mr-2" /> Agregar nueva suscripción
                        </Button>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSubDialogOpen(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Popup: Nueva Suscripción */}
            <Dialog open={nuevaSubDialogOpen} onOpenChange={setNuevaSubDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="nueva-suscripcion-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-blue-600" /> Nueva Suscripción
                        </DialogTitle>
                        <DialogDescription>
                            {subEmpresa?.nombre} — define la cantidad de documentos y el período de apertura (desde / hasta).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
                        <div className="md:col-span-2">
                            <Label>Plan</Label>
                            <Input value={subForm.plan_nombre} onChange={(e) => setSubForm({ ...subForm, plan_nombre: e.target.value })} placeholder="Plan Profesional" data-testid="sub-plan-input" />
                        </div>
                        <div>
                            <Label>Cantidad de documentos *</Label>
                            <Input type="number" min="1" value={subForm.cantidad_documentos} onChange={(e) => setSubForm({ ...subForm, cantidad_documentos: e.target.value })} placeholder="Ej. 500" data-testid="sub-cantidad-input" />
                        </div>
                        <div>
                            <Label>Precio (RD$)</Label>
                            <Input type="number" min="0" step="0.01" value={subForm.precio} onChange={(e) => setSubForm({ ...subForm, precio: e.target.value })} placeholder="Ej. 2500" data-testid="sub-precio-input" />
                        </div>
                        <div>
                            <Label>Fecha de apertura (desde) *</Label>
                            <Input type="date" value={subForm.fecha_desde} onChange={(e) => setSubForm({ ...subForm, fecha_desde: e.target.value })} data-testid="sub-desde-input" />
                        </div>
                        <div>
                            <Label>Fecha (hasta) *</Label>
                            <Input type="date" value={subForm.fecha_hasta} onChange={(e) => setSubForm({ ...subForm, fecha_hasta: e.target.value })} data-testid="sub-hasta-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label>Notas (opcional)</Label>
                            <Input value={subForm.notas} onChange={(e) => setSubForm({ ...subForm, notas: e.target.value })} placeholder="Observaciones..." data-testid="sub-notas-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNuevaSubDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleCrearSuscripcion} disabled={subSaving} data-testid="agregar-suscripcion-button">
                            {subSaving ? "Agregando..." : "Agregar Suscripción"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmación de eliminación de suscripción */}
            <AlertDialog open={!!subToDelete} onOpenChange={(open) => { if (!open) setSubToDelete(null); }}>
                <AlertDialogContent data-testid="eliminar-sub-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar suscripción?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {subToDelete && (
                                <>Esta acción no se puede deshacer. Se eliminará la suscripción <strong>{subToDelete.plan_nombre}</strong> ({subToDelete.documentos_contratados} documentos, {moment(subToDelete.fecha_inicio).format('YYYY-MM-DD')} → {moment(subToDelete.fecha_renovacion).format('YYYY-MM-DD')}).</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancelar-eliminar-sub">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={handleEliminarSuscripcion} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-eliminar-sub">
                            Eliminar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
